import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BaseButtonSize, BaseModalService, CustomerResponse } from 'base';
import { BaseCustomerCompanySettingsEnum } from '../../../_enums/company-settings.enum';
import { CompanySettingsFormInterface } from '../../../_interfaces/company-settings.interface';

@Component({
  selector: 'base-customer-company-settings-form',
  templateUrl: './company-settings-form.component.html',
})
export class BaseCustomerCompanySettingsFormComponent implements OnInit, OnDestroy {
  @ViewChild('updateCompanySettingModal', { static: true }) updateCompanySettingModal: ElementRef;

  @Input() customer: CustomerResponse | undefined;
  @Input() form: FormGroup<CompanySettingsFormInterface>;
  @Input() areEPAndMLPVisible: boolean;
  @Output() updateCompanySettings: EventEmitter<void> = new EventEmitter<void>();

  isFormSubmitted: boolean;
  changeSettingConfirmationText: string;
  changeSettingDescription: string;
  isEPAndMLPEditMode: boolean;
  modalRef: NgbModalRef;

  buttonSizes = BaseButtonSize;
  companySettings = BaseCustomerCompanySettingsEnum;

  private destroy$ = new Subject<void>();

  constructor(private modalService: BaseModalService) {}

  ngOnInit(): void {
    this.subscribeToCompanySettingsFormChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editEPAndMLP(): void {
    this.isEPAndMLPEditMode = true;
  }

  submitEPAndMLPUpdate(): void {
    this.isFormSubmitted = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.updateCompanySettings.emit();
    this.isEPAndMLPEditMode = false;
  }

  cancelEPAndMLPUpdate(): void {
    if (this.customer?.equalPay) {
      this.isFormSubmitted = false;
      this.form.markAsPristine();
      this.form.controls.equalPay.setValue(this.customer.equalPay);
      this.form.controls.maximumLeasePeriodDifference.setValue(this.customer.maximumLeasePeriodDifference);
      this.isEPAndMLPEditMode = false;
    }
  }

  private updateCompanySetting(settingName: BaseCustomerCompanySettingsEnum, updatedValue: boolean): void {
    this.defineChangeSettingsModalTexts(settingName, updatedValue);

    this.modalRef = this.modalService.open(
      {
        headText: `administration.companySettings.${updatedValue ? 'activateSetting' : 'deactivateSetting'}`,
        bodyTemplate: this.updateCompanySettingModal,
        okText: updatedValue ? 'action.yesActivate' : 'action.yesDeactivate',
      },
      {
        backdrop: 'static',
      }
    );

    this.modalRef.result.then(
      () => {
        this.updateCompanySettings.emit();
      },
      () => {
        if (!this.customer) {
          return;
        }

        switch (settingName) {
          case BaseCustomerCompanySettingsEnum.ASSIGNMENTS_LOCATION_ONLY:
            this.form.controls.assignmentsLocationOnly.setValue(this.customer.assignmentsLocationOnly, {
              emitEvent: false,
            });
            break;
          case BaseCustomerCompanySettingsEnum.EP_LOCATION_ONLY:
            this.form.controls.equalPayLocationOnly.setValue(this.customer.equalPayLocationOnly, { emitEvent: false });
            break;
          case BaseCustomerCompanySettingsEnum.RATINGS_LOCATION_ONLY:
            this.form.controls.ratingsLocationOnly.setValue(this.customer.ratingsLocationOnly, { emitEvent: false });
            break;
        }
      }
    );
  }

  private defineChangeSettingsModalTexts(settingName: BaseCustomerCompanySettingsEnum, updatedValue: boolean): void {
    this.changeSettingConfirmationText = updatedValue
      ? 'administration.companySettings.wantActivate'
      : 'administration.companySettings.wantDeactivate';

    this.changeSettingDescription = `administration.companySettings.${settingName}.${
      updatedValue ? 'activationWarning' : 'deactivationWarning'
    }`;
  }

  private subscribeToCompanySettingsFormChanges(): void {
    const controlsToWatch = [
      { name: 'assignmentsLocationOnly', enum: this.companySettings.ASSIGNMENTS_LOCATION_ONLY },
      { name: 'equalPayLocationOnly', enum: this.companySettings.EP_LOCATION_ONLY },
      { name: 'ratingsLocationOnly', enum: this.companySettings.RATINGS_LOCATION_ONLY },
    ];

    controlsToWatch.forEach((control) => {
      this.form
        .get(control.name)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((newValue: boolean) => {
          this.updateCompanySetting(control.enum, newValue);
        });
    });
  }
}
